import { useMutation } from '@tanstack/react-query';
import { UpdateOrganizationCoachingPayLaterEnabledDto } from 'models/organization/dto/organization.dto';
import { useApiClient } from '../../../apiClient';
import { useSelectedOrganization } from './useSelectedOrganization';

// Per-tenant gate for the coaching "Pay later" option. Hits
// POST :id/coaching-pay-later-enabled on the API. The endpoint returns only the
// flipped flag ({ coachingPayLaterEnabled }) — not the full aggregate — so we
// patch it into the cached selectedOrganization locally rather than blowing the
// whole org back into the Zustand store. Mirrors
// useUpdateOrganizationPayForOthersEnabled in shape and intent.
export const useUpdateOrganizationCoachingPayLaterEnabled = (
    organizationId: string,
    onSuccess: () => void,
) => {
    const apiClient = useApiClient();
    const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();

    return useMutation({
        mutationKey: ['updateOrganizationCoachingPayLaterEnabled', organizationId],
        mutationFn: (body: UpdateOrganizationCoachingPayLaterEnabledDto) =>
            apiClient.fetchApi<{ coachingPayLaterEnabled: boolean }, UpdateOrganizationCoachingPayLaterEnabledDto>({
                method: 'POST',
                endpoint: `organizations/${organizationId}/coaching-pay-later-enabled`,
                body,
            }),
        onSuccess: (data) => {
            onSuccess();
            // Only update the selected-org cache when the response is for the
            // org currently in view — defensive against the rare case where
            // the operator switches orgs while the mutation is in flight.
            if (selectedOrganization && organizationId === selectedOrganization.id) {
                setSelectedOrganization({
                    ...selectedOrganization,
                    coachingPayLaterEnabled: data.coachingPayLaterEnabled,
                });
            }
        },
    });
};
